// Shared noise sources, created once per CarAudio. Every noise-based layer
// (rasp, squeal, wind, kerbs, pops, impacts) taps the same looping white-noise
// source through its own filter, which keeps the node count low.

import { whiteNoise } from './mapping';

export class NoiseBank {
  /** Looping white noise at the context rate. */
  readonly white: AudioBufferSourceNode;
  /**
   * Slow random modulation signal (±1, ~12 random points/s) for pitch jitter
   * and squeal warble: a short noise buffer played back very slowly.
   */
  readonly jitter: AudioBufferSourceNode;
  /** Random amplitude modulation for gravel/grass; its rate is set per surface. */
  readonly surfaceRandom: AudioBufferSourceNode;

  constructor(readonly ctx: BaseAudioContext) {
    const sr = ctx.sampleRate;
    const whiteBuf = ctx.createBuffer(1, Math.round(sr * 3), sr);
    whiteBuf.getChannelData(0).set(whiteNoise(whiteBuf.length, 1));
    const slowBuf = ctx.createBuffer(1, 4096, sr);
    slowBuf.getChannelData(0).set(whiteNoise(4096, 2));

    this.white = this.loop(whiteBuf, 1);
    this.jitter = this.loop(slowBuf, this.rateForHz(12));
    this.surfaceRandom = this.loop(slowBuf, this.rateForHz(40));
  }

  /** Playback rate that steps through `hz` random points per second. */
  rateForHz(hz: number): number {
    return hz / this.ctx.sampleRate;
  }

  private loop(buffer: AudioBuffer, rate: number): AudioBufferSourceNode {
    const src = this.ctx.createBufferSource();
    src.buffer = buffer;
    src.loop = true;
    src.playbackRate.value = rate;
    src.start();
    return src;
  }

  dispose(): void {
    for (const s of [this.white, this.jitter, this.surfaceRandom]) {
      try {
        s.stop();
      } catch {
        // Already stopped.
      }
      s.disconnect();
    }
  }
}
